import { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

// Comprehensive translations for the entire app
const translations = {
  fr: {
    // Navigation
    dashboard: 'Tableau de bord',
    nutrition: 'Nutrition',
    workouts: 'Entraînements',
    scanner: 'Scanner',
    community: 'Communauté',
    profile: 'Profil',
    bariatric: 'By-pass/Sleeve',
    progress: 'Progrès',
    home: 'Accueil',
    sport: 'Sport',
    
    // Common
    loading: 'Chargement...',
    save: 'Enregistrer',
    cancel: 'Annuler',
    delete: 'Supprimer',
    edit: 'Modifier',
    share: 'Partager',
    close: 'Fermer',
    back: 'Retour',
    next: 'Suivant',
    finish: 'Terminer',
    search: 'Rechercher',
    filter: 'Filtrer',
    add: 'Ajouter',
    remove: 'Retirer',
    confirm: 'Confirmer',
    today: 'Aujourd\'hui',
    yesterday: 'Hier',
    tomorrow: 'Demain',
    week: 'Semaine',
    month: 'Mois',
    year: 'Année',
    all: 'Tout',
    none: 'Aucun',
    yes: 'Oui',
    no: 'Non',
    ok: 'OK',
    
    // Dashboard
    welcome: 'Bienvenue',
    hello: 'Bonjour',
    dailyChallenges: 'Défis du jour',
    appointments: 'Rendez-vous',
    todayRecipes: 'Recettes du jour',
    caloriesConsumed: 'Calories consommées',
    caloriesBurned: 'Calories brûlées',
    waterIntake: 'Eau consommée',
    dailySummary: 'Résumé du jour',
    quickActions: 'Actions rapides',
    recentActivity: 'Activité récente',
    motivation: 'Motivation',
    goals: 'Objectifs',
    todayGoal: 'Objectif du jour',
    remainingCalories: 'Calories restantes',
    seeMore: 'Voir plus',
    seeAll: 'Tout voir',
    
    // Nutrition
    meals: 'Repas',
    breakfast: 'Petit-déjeuner',
    lunch: 'Déjeuner',
    dinner: 'Dîner',
    snack: 'Collation',
    recipes: 'Recettes',
    ingredients: 'Ingrédients',
    preparation: 'Préparation',
    nutritionalInfo: 'Informations nutritionnelles',
    calories: 'Calories',
    proteins: 'Protéines',
    carbs: 'Glucides',
    fats: 'Lipides',
    fiber: 'Fibres',
    addMeal: 'Ajouter un repas',
    logFood: 'Enregistrer un aliment',
    searchFood: 'Rechercher un aliment',
    scanBarcode: 'Scanner un code-barres',
    mealPlan: 'Plan de repas',
    shoppingList: 'Liste de courses',
    
    // Workouts
    allCategories: 'Toutes',
    favorites: 'Favoris',
    addToFavorites: 'Ajouter aux favoris',
    removeFromFavorites: 'Retirer des favoris',
    addToAgenda: 'Ajouter à l\'agenda',
    shareWorkout: 'Partager',
    completed: 'Terminé !',
    videos: 'Vidéos',
    programs: 'Programmes',
    history: 'Historique',
    duration: 'Durée',
    difficulty: 'Difficulté',
    beginner: 'Débutant',
    intermediate: 'Intermédiaire',
    expert: 'Expert',
    equipment: 'Équipement',
    noEquipment: 'Sans équipement',
    startWorkout: 'Commencer',
    logWorkout: 'Enregistrer un entraînement',
    caloriesBurnedWorkout: 'Calories brûlées',
    aiCoach: 'Coach IA',
    createProgram: 'Créer un programme',
    
    // Progress
    weight: 'Poids',
    currentWeight: 'Poids actuel',
    targetWeight: 'Poids cible',
    weightProgress: 'Progression du poids',
    bmi: 'IMC',
    steps: 'Pas',
    stepsToday: 'Pas aujourd\'hui',
    stepsGoal: 'Objectif de pas',
    badges: 'Badges',
    achievements: 'Réussites',
    statistics: 'Statistiques',
    downloadReport: 'Télécharger le rapport',
    generatePdf: 'Générer PDF',
    addWeight: 'Ajouter le poids',
    addSteps: 'Ajouter les pas',
    progressChart: 'Graphique de progression',
    weeklyProgress: 'Progression hebdomadaire',
    monthlyProgress: 'Progression mensuelle',
    
    // Social/Community
    publicFeed: 'Fil public',
    groups: 'Groupes',
    friends: 'Amis',
    leaderboard: 'Classement',
    newPost: 'Nouveau post',
    writePost: 'Écrire une publication...',
    publish: 'Publier',
    like: 'J\'aime',
    comment: 'Commenter',
    comments: 'Commentaires',
    writeComment: 'Écrire un commentaire...',
    noComments: 'Aucun commentaire',
    noPosts: 'Aucune publication',
    joinGroup: 'Rejoindre',
    leaveGroup: 'Quitter',
    members: 'Membres',
    rank: 'Rang',
    points: 'Points',
    
    // Profile
    settings: 'Paramètres',
    language: 'Langue',
    logout: 'Déconnexion',
    deleteAccount: 'Supprimer le compte',
    notifications: 'Notifications',
    privacy: 'Confidentialité',
    help: 'Aide',
    about: 'À propos',
    darkMode: 'Mode sombre',
    lightMode: 'Mode clair',
    personalInfo: 'Informations personnelles',
    healthInfo: 'Informations de santé',
    age: 'Âge',
    height: 'Taille',
    gender: 'Genre',
    male: 'Homme',
    female: 'Femme',
    other: 'Autre',
    activityLevel: 'Niveau d\'activité',
    premium: 'Premium',
    upgradeToPremium: 'Passer à Premium',
    premiumFeatures: 'Fonctionnalités Premium',
    resetProgress: 'Réinitialiser la progression',
    editProfile: 'Modifier le profil',
    
    // Messages
    success: 'Succès',
    error: 'Erreur',
    saved: 'Enregistré',
    shared: 'Partagé',
    deleted: 'Supprimé',
    updated: 'Mis à jour',
    added: 'Ajouté',
    removed: 'Retiré',
    logoutSuccess: 'Déconnexion réussie',
    saveSuccess: 'Enregistré avec succès',
    errorOccurred: 'Une erreur est survenue',
    tryAgain: 'Réessayer',
    noData: 'Aucune donnée',
    noResults: 'Aucun résultat',
    
    // Scanner
    scanFood: 'Scanner un aliment',
    takePhoto: 'Prendre une photo',
    choosePhoto: 'Choisir une photo',
    analyzing: 'Analyse en cours...',
    identifiedFood: 'Aliment identifié',
    
    // PDF Report
    progressReport: 'Rapport de progression',
    generatedOn: 'Généré le',
    profileSection: 'Profil',
    weightSection: 'Progression du poids',
    nutritionSection: 'Statistiques nutritionnelles',
    activitySection: 'Activité physique',
    totalMeals: 'Repas enregistrés',
    totalCalories: 'Calories totales',
    dailyAverage: 'Moyenne journalière',
    totalSteps: 'Pas totaux',
    daysTracked: 'Jours suivis',
    initialWeight: 'Poids initial',
    weightChange: 'Variation',
    initialBmi: 'IMC initial',
    currentBmi: 'IMC actuel',

    // Landing page
    landingNavLogin: 'Connexion',
    landingBadge: 'Coaching IA personnalisé',
    landingHeroTitle: 'Transformez votre corps avec',
    landingHeroAccent: "l'intelligence artificielle",
    landingHeroSubtitle: 'Fat & Slim analyse votre profil, vos habitudes et vos objectifs pour créer un programme nutrition et sport 100% personnalisé.',
    landingStartFree: 'Commencer gratuitement',
    landingHaveAccount: "J'ai déjà un compte",
    landingSoon: 'Bientôt',
    landingActiveUsers: 'utilisateurs actifs',
    landingFeatScanTitle: 'Scan IA',
    landingFeatScanDesc: 'Photographiez vos repas pour une analyse nutritionnelle instantanée',
    landingFeatMenuTitle: 'Menus personnalisés',
    landingFeatMenuDesc: 'Plans alimentaires adaptés à vos objectifs et préférences',
    landingFeatCoachTitle: 'Coaching sportif',
    landingFeatCoachDesc: "Programmes d'entraînement sur mesure pour tous niveaux",
    landingFeatTrackTitle: 'Suivi intelligent',
    landingFeatTrackDesc: 'Visualisez votre progression avec des statistiques détaillées',
    landingFeaturesTitle: 'Tout ce dont vous avez besoin',
    landingFeaturesSubtitle: 'Une application complète pour atteindre vos objectifs de forme et de santé',
    landingScanTitle: 'Scannez, analysez, améliorez',
    landingScanDesc: "Notre IA reconnaît instantanément vos aliments et calcule les valeurs nutritionnelles. Obtenez un score Nutri-Score et des recommandations personnalisées pour chaque repas.",
    landingScanFeat1: 'Reconnaissance photo des aliments',
    landingScanFeat2: 'Calcul calories, protéines, glucides, lipides',
    landingScanFeat3: 'Score nutritionnel de A à E',
    landingScanFeat4: "Suggestions d'alternatives plus saines",
    landingCtaTitle: 'Prêt à transformer votre vie ?',
    landingCtaSubtitle: "Rejoignez des milliers d'utilisateurs qui ont déjà atteint leurs objectifs grâce à Fat & Slim",
    landingStartNow: 'Commencer maintenant',
    landingDownloadApp: "Téléchargez l'application",
    landingRights: '© 2025 Fat & Slim. Tous droits réservés.',

    // Login / Register
    loginWelcome: 'Bon retour !',
    loginSubtitle: 'Connectez-vous pour continuer votre parcours',
    emailLabel: 'Email',
    passwordLabel: 'Mot de passe',
    loginLoading: 'Connexion...',
    loginSubmit: 'Se connecter',
    orSeparator: 'ou',
    continueWithGoogle: 'Continuer avec Google',
    noAccountYet: 'Pas encore de compte ?',
    signUpLink: "S'inscrire",
    loginSuccess: 'Connexion réussie !',
    loginErrorMsg: 'Erreur de connexion',
    registerTitle: 'Créer un compte',
    registerSubtitle: "Commencez votre transformation dès aujourd'hui",
    firstNameLabel: 'Prénom',
    firstNamePlaceholder: 'Votre prénom',
    emailPlaceholder: 'votre@email.com',
    passwordMinHint: 'Minimum 6 caractères',
    registerLoading: 'Création...',
    registerSubmit: 'Créer mon compte',
    alreadyAccount: 'Déjà un compte ?',
    registerSuccess: 'Compte créé avec succès !',
    registerErrorMsg: "Erreur lors de l'inscription",
    passwordTooShort: 'Le mot de passe doit contenir au moins 6 caractères',
  },
  en: {
    // Navigation
    dashboard: 'Dashboard',
    nutrition: 'Nutrition',
    workouts: 'Workouts',
    scanner: 'Scanner',
    community: 'Community',
    profile: 'Profile',
    bariatric: 'Bypass/Sleeve',
    progress: 'Progress',
    home: 'Home',
    sport: 'Sport',
    
    // Common
    loading: 'Loading...',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    share: 'Share',
    close: 'Close',
    back: 'Back',
    next: 'Next',
    finish: 'Finish',
    search: 'Search',
    filter: 'Filter',
    add: 'Add',
    remove: 'Remove',
    confirm: 'Confirm',
    today: 'Today',
    yesterday: 'Yesterday',
    tomorrow: 'Tomorrow',
    week: 'Week',
    month: 'Month',
    year: 'Year',
    all: 'All',
    none: 'None',
    yes: 'Yes',
    no: 'No',
    ok: 'OK',
    
    // Dashboard
    welcome: 'Welcome',
    hello: 'Hello',
    dailyChallenges: 'Daily Challenges',
    appointments: 'Appointments',
    todayRecipes: 'Today\'s Recipes',
    caloriesConsumed: 'Calories Consumed',
    caloriesBurned: 'Calories Burned',
    waterIntake: 'Water Intake',
    dailySummary: 'Daily Summary',
    quickActions: 'Quick Actions',
    recentActivity: 'Recent Activity',
    motivation: 'Motivation',
    goals: 'Goals',
    todayGoal: 'Today\'s Goal',
    remainingCalories: 'Remaining Calories',
    seeMore: 'See more',
    seeAll: 'See all',
    
    // Nutrition
    meals: 'Meals',
    breakfast: 'Breakfast',
    lunch: 'Lunch',
    dinner: 'Dinner',
    snack: 'Snack',
    recipes: 'Recipes',
    ingredients: 'Ingredients',
    preparation: 'Preparation',
    nutritionalInfo: 'Nutritional Info',
    calories: 'Calories',
    proteins: 'Proteins',
    carbs: 'Carbs',
    fats: 'Fats',
    fiber: 'Fiber',
    addMeal: 'Add Meal',
    logFood: 'Log Food',
    searchFood: 'Search food',
    scanBarcode: 'Scan Barcode',
    mealPlan: 'Meal Plan',
    shoppingList: 'Shopping List',
    
    // Workouts
    allCategories: 'All',
    favorites: 'Favorites',
    addToFavorites: 'Add to favorites',
    removeFromFavorites: 'Remove from favorites',
    addToAgenda: 'Add to agenda',
    shareWorkout: 'Share',
    completed: 'Completed!',
    videos: 'Videos',
    programs: 'Programs',
    history: 'History',
    duration: 'Duration',
    difficulty: 'Difficulty',
    beginner: 'Beginner',
    intermediate: 'Intermediate',
    expert: 'Expert',
    equipment: 'Equipment',
    noEquipment: 'No equipment',
    startWorkout: 'Start',
    logWorkout: 'Log workout',
    caloriesBurnedWorkout: 'Calories burned',
    aiCoach: 'AI Coach',
    createProgram: 'Create Program',
    
    // Progress
    weight: 'Weight',
    currentWeight: 'Current Weight',
    targetWeight: 'Target Weight',
    weightProgress: 'Weight Progress',
    bmi: 'BMI',
    steps: 'Steps',
    stepsToday: 'Steps Today',
    stepsGoal: 'Steps Goal',
    badges: 'Badges',
    achievements: 'Achievements',
    statistics: 'Statistics',
    downloadReport: 'Download Report',
    generatePdf: 'Generate PDF',
    addWeight: 'Add Weight',
    addSteps: 'Add Steps',
    progressChart: 'Progress Chart',
    weeklyProgress: 'Weekly Progress',
    monthlyProgress: 'Monthly Progress',
    
    // Social/Community
    publicFeed: 'Public Feed',
    groups: 'Groups',
    friends: 'Friends',
    leaderboard: 'Leaderboard',
    newPost: 'New Post',
    writePost: 'Write a post...',
    publish: 'Publish',
    like: 'Like',
    comment: 'Comment',
    comments: 'Comments',
    writeComment: 'Write a comment...',
    noComments: 'No comments',
    noPosts: 'No posts',
    joinGroup: 'Join',
    leaveGroup: 'Leave',
    members: 'Members',
    rank: 'Rank',
    points: 'Points',
    
    // Profile
    settings: 'Settings',
    language: 'Language',
    logout: 'Logout',
    deleteAccount: 'Delete Account',
    notifications: 'Notifications',
    privacy: 'Privacy',
    help: 'Help',
    about: 'About',
    darkMode: 'Dark Mode',
    lightMode: 'Light Mode',
    personalInfo: 'Personal Info',
    healthInfo: 'Health Info',
    age: 'Age',
    height: 'Height',
    gender: 'Gender',
    male: 'Male',
    female: 'Female',
    other: 'Other',
    activityLevel: 'Activity Level',
    premium: 'Premium',
    upgradeToPremium: 'Upgrade to Premium',
    premiumFeatures: 'Premium Features',
    resetProgress: 'Reset Progress',
    editProfile: 'Edit Profile',
    
    // Messages
    success: 'Success',
    error: 'Error',
    saved: 'Saved',
    shared: 'Shared',
    deleted: 'Deleted',
    updated: 'Updated',
    added: 'Added',
    removed: 'Removed',
    logoutSuccess: 'Successfully logged out',
    saveSuccess: 'Saved successfully',
    errorOccurred: 'An error occurred',
    tryAgain: 'Try again',
    noData: 'No data',
    noResults: 'No results',
    
    // Scanner
    scanFood: 'Scan Food',
    takePhoto: 'Take Photo',
    choosePhoto: 'Choose Photo',
    analyzing: 'Analyzing...',
    identifiedFood: 'Identified Food',
    
    // PDF Report
    progressReport: 'Progress Report',
    generatedOn: 'Generated on',
    profileSection: 'Profile',
    weightSection: 'Weight Progress',
    nutritionSection: 'Nutrition Statistics',
    activitySection: 'Physical Activity',
    totalMeals: 'Meals Logged',
    totalCalories: 'Total Calories',
    dailyAverage: 'Daily Average',
    totalSteps: 'Total Steps',
    daysTracked: 'Days Tracked',
    initialWeight: 'Initial Weight',
    weightChange: 'Change',
    initialBmi: 'Initial BMI',
    currentBmi: 'Current BMI',

    // Landing page
    landingNavLogin: 'Login',
    landingBadge: 'Personalized AI Coaching',
    landingHeroTitle: 'Transform your body with',
    landingHeroAccent: 'artificial intelligence',
    landingHeroSubtitle: 'Fat & Slim analyzes your profile, habits and goals to create a 100% personalized nutrition and fitness program.',
    landingStartFree: 'Start for free',
    landingHaveAccount: 'I already have an account',
    landingSoon: 'Soon',
    landingActiveUsers: 'active users',
    landingFeatScanTitle: 'AI Scan',
    landingFeatScanDesc: 'Photograph your meals for instant nutritional analysis',
    landingFeatMenuTitle: 'Personalized Menus',
    landingFeatMenuDesc: 'Meal plans tailored to your goals and preferences',
    landingFeatCoachTitle: 'Fitness Coaching',
    landingFeatCoachDesc: 'Custom workout programs for all levels',
    landingFeatTrackTitle: 'Smart Tracking',
    landingFeatTrackDesc: 'Visualize your progress with detailed statistics',
    landingFeaturesTitle: 'Everything you need',
    landingFeaturesSubtitle: 'A complete app to reach your fitness and health goals',
    landingScanTitle: 'Scan, analyze, improve',
    landingScanDesc: 'Our AI instantly recognizes your food and calculates nutritional values. Get a Nutri-Score and personalized recommendations for every meal.',
    landingScanFeat1: 'Photo food recognition',
    landingScanFeat2: 'Calorie, protein, carb, fat calculation',
    landingScanFeat3: 'Nutrition score from A to E',
    landingScanFeat4: 'Healthier alternative suggestions',
    landingCtaTitle: 'Ready to transform your life?',
    landingCtaSubtitle: 'Join thousands of users who have already reached their goals with Fat & Slim',
    landingStartNow: 'Start now',
    landingDownloadApp: 'Download the app',
    landingRights: '© 2025 Fat & Slim. All rights reserved.',

    // Login / Register
    loginWelcome: 'Welcome back!',
    loginSubtitle: 'Sign in to continue your journey',
    emailLabel: 'Email',
    passwordLabel: 'Password',
    loginLoading: 'Signing in...',
    loginSubmit: 'Sign in',
    orSeparator: 'or',
    continueWithGoogle: 'Continue with Google',
    noAccountYet: "Don't have an account yet?",
    signUpLink: 'Sign up',
    loginSuccess: 'Login successful!',
    loginErrorMsg: 'Login error',
    registerTitle: 'Create an account',
    registerSubtitle: 'Start your transformation today',
    firstNameLabel: 'First name',
    firstNamePlaceholder: 'Your first name',
    emailPlaceholder: 'your@email.com',
    passwordMinHint: 'Minimum 6 characters',
    registerLoading: 'Creating...',
    registerSubmit: 'Create my account',
    alreadyAccount: 'Already have an account?',
    registerSuccess: 'Account created successfully!',
    registerErrorMsg: 'Registration error',
    passwordTooShort: 'Password must be at least 6 characters',
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'fr';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.lang = language;
  }, [language]);

  const t = (key) => {
    return translations[language]?.[key] || translations.fr[key] || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'fr' ? 'en' : 'fr');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

export default LanguageContext;
